import footTrafficReducer from '@/store/slices/attribution/footTraffic.slice';
import offlineSalesReducer from '@/store/slices/attribution/offlineSales.slice';
import webVisitsReducer from '@/store/slices/attribution/webVisits.slice';
import dv360TicketsCreativesReducer from '@/store/slices/dv360Tickets/creatives.slice';
import dv360TicketsInsertionOrdersReducer from '@/store/slices/dv360Tickets/insertionOrderManagement.slice';
import sharesUsersReducer from '@/store/slices/sharesUsers.slice';
import { combineReducers, configureStore, isAnyOf } from '@reduxjs/toolkit';
import advertisersReducer from './slices/advertisers.slice';
import agenciesReducer from './slices/agencies.slice';
import audienceTemplatesReducer from './slices/audienceTemplates.slice';
import authReducer, { enterImpersonationMode, exitImpersonationMode, logOut } from './slices/authSlice';
import campaignManagementReducer from './slices/campaignManagementSlice';
import campaignReducer from './slices/campaignSlice';
import changeLogHistoryReducer from './slices/changeLogHistory.slice';
import clientsReducer from './slices/clients.slice';
import creativeReducer from './slices/creativeSlice';
import currentWhitelabelReducer from './slices/currentWhitelabelSlice';
import firstPartyDataReducer from './slices/firstPartyDataSlice';
import forecasterReducer from './slices/forecasterSlice';
import geoReducer from './slices/geoSlice';
import meReducer from './slices/meSlice';
import pacingReducer from './slices/pacing';
import pdfBuilderReducer from './slices/pdfBuilder.slice';
import pixelsSlice from './slices/pixelsSlice';
import beeswaxReportBuilderSlice from './slices/reportBuilder/beeswaxSlice';
import omniplatformReportBuilderSlice from './slices/reportBuilder/omniplatformSlice';
import youtubeReportBuilderSlice from './slices/reportBuilder/youtubeSlice';
import mainReportingReducer from './slices/reportingDashboard';
import reportingDashboardAdswizzReducer from './slices/reportingDashboard/adswizzSlice';
import reportingDashboardBeeswaxReducer from './slices/reportingDashboard/beeswaxSlice';
import reportingDashboardGamReducer from './slices/reportingDashboard/gamSlice';
import reportingDashboardGoogleReducer from './slices/reportingDashboard/googleSlice';
import reportingDashboardNeuronReducer from './slices/reportingDashboard/neuronSlice';
import reportingDashboardPrintPdfOptionsReducer from './slices/reportingDashboard/printPdfOptionsSlice';
import reportingDashboardSearchReducer from './slices/reportingDashboard/searchSlice';
import reportingDashboardSimplifiReducer from './slices/reportingDashboard/simplifiSlice';
import reportingDashboardSocialReducer from './slices/reportingDashboard/socialSlice';
import columnWidthsReducer from './slices/reportingV2/columnWidthsSlice';
import reportingV2Reducer from './slices/reportingV2/reportingV2Slice';
import segmentsSlice from './slices/segments.slice';
import sharedApiReducer from './slices/sharedApi.slice';
import userManagementReducer from './slices/userManagementSlice';
import whitelabelsReducer from './slices/whitelabelsSlice';

const appReducer = combineReducers({
  auth: authReducer,
  campaign: campaignReducer,
  creative: creativeReducer,
  userManagement: userManagementReducer,
  currentWhitelabel: currentWhitelabelReducer,
  whitelabels: whitelabelsReducer,
  me: meReducer,
  reportingDashboard: mainReportingReducer,
  reportingDashboardGoogle: reportingDashboardGoogleReducer,
  reportingDashboardNeuron: reportingDashboardNeuronReducer,
  reportingDashboardBeeswax: reportingDashboardBeeswaxReducer,
  reportingDashboardSearch: reportingDashboardSearchReducer,
  reportingDashboardSocial: reportingDashboardSocialReducer,
  reportingDashboardAdswizz: reportingDashboardAdswizzReducer,
  reportingDashboardSimplifi: reportingDashboardSimplifiReducer,
  reportingDashboardGam: reportingDashboardGamReducer,
  reportingV2: reportingV2Reducer,
  columnWidths: columnWidthsReducer,
  reportingDashboardPrintPdfOptions: reportingDashboardPrintPdfOptionsReducer,
  beeswaxReportBuilder: beeswaxReportBuilderSlice,
  youtubeReportBuilder: youtubeReportBuilderSlice,
  omniplatformReportBuilder: omniplatformReportBuilderSlice,
  pdfBuilder: pdfBuilderReducer,
  pixels: pixelsSlice,
  segments: segmentsSlice,
  geoData: geoReducer,

  pacing: pacingReducer,

  campaignManagement: campaignManagementReducer,
  forecaster: forecasterReducer,
  firstPartyData: firstPartyDataReducer,
  sharesUsers: sharesUsersReducer,
  advertisers: advertisersReducer,
  sharedApi: sharedApiReducer,
  agencies: agenciesReducer,
  audienceTemplates: audienceTemplatesReducer,
  changeLogHistory: changeLogHistoryReducer,
  clients: clientsReducer,
  webVisits: webVisitsReducer,
  offlineSales: offlineSalesReducer,
  footTraffic: footTrafficReducer,

  dv360TicketsCreatives: dv360TicketsCreativesReducer,
  dv360TicketsInsertionOrders: dv360TicketsInsertionOrdersReducer,
});

const isIdentityTransition = isAnyOf(enterImpersonationMode.fulfilled, exitImpersonationMode.fulfilled, logOut);

// Wipe per-user caches on logout and when impersonation enters/exits so the next identity
// doesn't see stale data. Preserve auth (carries the new token) and
// currentWhitelabel (domain-based, only fetched once on app mount); the rest
// re-initialize via combineReducers' default-state behavior.
const rootReducer: typeof appReducer = (state, action) => {
  if (state && isIdentityTransition(action)) {
    const preserved: Partial<ReturnType<typeof appReducer>> = {
      auth: state.auth,
      currentWhitelabel: state.currentWhitelabel,
    };
    return appReducer(preserved as Parameters<typeof appReducer>[0], action);
  }
  return appReducer(state, action);
};

const store = configureStore({
  reducer: rootReducer,
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;
